import axios from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';

import {
	getEmKeycloakUsersRequest,
	getEmKeycloakUsersSuccess,
	getEmKeycloakUsersFailure,
	getKeycloakGroupsRequest,
	getKeycloakGroupsSuccess,
	getKeycloakGroupsFailure,
	getKeycloakUsersRequest,
	getKeycloakUsersSuccess,
	getKeycloakUsersFailure,
	attachEmUserRequest,
	attachEmUserSuccess,
	attachEmUserFailure,
	detachEmUserRequest,
	detachEmUserSuccess,
	detachEmUserFailure,
	createKeycloakUserRequest,
	createKeycloakUserSuccess,
	createKeycloakUserFailure,
	setKeycloakUserEnabledRequest,
	setKeycloakUserEnabledSuccess,
	setKeycloakUserEnabledFailure,
	resetKeycloakUserPasswordRequest,
	resetKeycloakUserPasswordSuccess,
	resetKeycloakUserPasswordFailure,
	getConnectedEmerchantsRequest,
	getConnectedEmerchantsSuccess,
	getConnectedEmerchantsFailure,
} from './reducer';

import {
	TEmKeycloakUsersRequest,
	TEmKeycloakUsersResponsePayload,
	TKeycloakGroupsRequest,
	TKeycloakGroupsResponsePayload,
	TKeycloakUsersRequest,
	TKeycloakUsersResponsePayload,
	TAttachEmKeycloakUserRequest,
	TDetachEmKeycloakUserRequest,
	TCreateKeycloakUserRequest,
	TSetKeycloakUserEnabledRequest,
	TResetKeycloakUserPasswordRequest,
	TConnectedEmerchantsRequest,
	TConnectedEmerchantsResponsePayload,
} from './types';

function* getEmKeycloakUsersRequestWorker({ payload }: PayloadAction<TEmKeycloakUsersRequest>) {
	try {
		const response: TEmKeycloakUsersResponsePayload = yield call(
			api.accessManagement.getEmKeycloakUsers,
			payload,
		);
		yield put(getEmKeycloakUsersSuccess(response));
	} catch (error) {
		yield put(getEmKeycloakUsersFailure());
		if (axios.isAxiosError(error) && error.response?.status !== 404) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* getKeycloakGroupsRequestWorker({ payload }: PayloadAction<TKeycloakGroupsRequest>) {
	try {
		const response: TKeycloakGroupsResponsePayload = yield call(
			api.accessManagement.getKeycloakGroups,
			payload,
		);
		yield put(getKeycloakGroupsSuccess(response));
	} catch (error) {
		yield put(getKeycloakGroupsFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* getKeycloakUsersRequestWorker({ payload }: PayloadAction<TKeycloakUsersRequest>) {
	try {
		const response: TKeycloakUsersResponsePayload = yield call(
			api.accessManagement.getKeycloakUsers,
			payload,
		);
		yield put(getKeycloakUsersSuccess(response));
	} catch (error) {
		yield put(getKeycloakUsersFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* attachEmUserRequestWorker({ payload }: PayloadAction<TAttachEmKeycloakUserRequest>) {
	try {
		yield call(api.accessManagement.attachEmUser, payload.body);
		yield put(attachEmUserSuccess());
		notificationContainer('User attached successfully', 'success');
		yield put(getEmKeycloakUsersRequest(payload.getEmKeycloakUsers));
	} catch (error) {
		yield put(attachEmUserFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* detachEmUserRequestWorker({ payload }: PayloadAction<TDetachEmKeycloakUserRequest>) {
	try {
		yield call(api.accessManagement.detachEmUser, payload.body);
		yield put(detachEmUserSuccess());
		notificationContainer('User detached successfully', 'success');

		if (payload.getEmKeycloakUsers) {
			yield put(getEmKeycloakUsersRequest(payload.getEmKeycloakUsers));
		}
		yield put(getConnectedEmerchantsRequest({ email: payload.body.email }));
	} catch (error) {
		yield put(detachEmUserFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* createKeycloakUserRequestWorker({ payload }: PayloadAction<TCreateKeycloakUserRequest>) {
	try {
		yield call(api.accessManagement.createUser, payload.body);
		yield put(createKeycloakUserSuccess());
		notificationContainer('User created successfully', 'success');
		yield put(getEmKeycloakUsersRequest(payload.getEmKeycloakUsers));
	} catch (error) {
		yield put(createKeycloakUserFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* setKeycloakUserEnabledRequestWorker({
	payload,
}: PayloadAction<TSetKeycloakUserEnabledRequest>) {
	try {
		yield call(api.accessManagement.setUserEnabled, payload.body);
		yield put(setKeycloakUserEnabledSuccess());
		notificationContainer('User status updated', 'success');
		yield put(getEmKeycloakUsersRequest(payload.getEmKeycloakUsers));
	} catch (error) {
		yield put(setKeycloakUserEnabledFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* resetKeycloakUserPasswordRequestWorker({
	payload,
}: PayloadAction<TResetKeycloakUserPasswordRequest>) {
	try {
		yield call(api.accessManagement.resetUserPassword, payload);
		yield put(resetKeycloakUserPasswordSuccess());
		notificationContainer('Password reset email sent', 'success');
	} catch (error) {
		yield put(resetKeycloakUserPasswordFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* getConnectedEmerchantsRequestWorker({
	payload,
}: PayloadAction<TConnectedEmerchantsRequest>) {
	try {
		const response: TConnectedEmerchantsResponsePayload = yield call(
			api.accessManagement.getConnectedEmerchants,
			payload,
		);
		yield put(getConnectedEmerchantsSuccess(response));
	} catch (error) {
		yield put(getConnectedEmerchantsFailure());
		if (axios.isAxiosError(error)) {
			// notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

export function* accessManagementSaga() {
	yield takeEvery(getEmKeycloakUsersRequest.type, getEmKeycloakUsersRequestWorker);
	yield takeEvery(getKeycloakGroupsRequest.type, getKeycloakGroupsRequestWorker);
	yield takeEvery(getKeycloakUsersRequest.type, getKeycloakUsersRequestWorker);
	yield takeEvery(attachEmUserRequest.type, attachEmUserRequestWorker);
	yield takeEvery(detachEmUserRequest.type, detachEmUserRequestWorker);
	yield takeEvery(createKeycloakUserRequest.type, createKeycloakUserRequestWorker);
	yield takeEvery(setKeycloakUserEnabledRequest.type, setKeycloakUserEnabledRequestWorker);
	yield takeEvery(resetKeycloakUserPasswordRequest.type, resetKeycloakUserPasswordRequestWorker);
	yield takeEvery(getConnectedEmerchantsRequest.type, getConnectedEmerchantsRequestWorker);
}
